import {
  type Face,
  INK,
  MARK,
  MASK_BODY,
  type Pose,
  body,
  eye,
  gladFace,
  shut,
  stroke,
  wave,
} from '../face-kit';
import { TAU, type Ctx } from '../paint';

const HEAD = { rx: 32, ry: 27 };
const EYE = { x: 13.5, y: -4, rx: 10.5, ry: 13, tilt: 0.3, facet: 2.7 };
const WING = { x: 31, y: -8, rx: 31, ry: 10, tilt: 0.5, hum: 0.05, ghost: 0.09 };
const NEEDLE = { from: 22, to: 66, half: 3.5 };
const FEELER = { root: [7, -23], bend: [12, -46], tip: [28, -57], plumes: 8 } as const;
const FAINT = 'rgb(255 255 255 / 0.35)';
const SOFT = 'rgb(255 255 255 / 0.7)';
const VEIL = 'rgb(255 255 255 / 0.1)';

export const NEEDLE_TIP: readonly [number, number] = [0, NEEDLE.to];

function wing(ctx: Ctx, side: number, lift: number): void {
  for (const [shift, alpha] of [
    [-WING.ghost, 0.45],
    [WING.ghost, 0.45],
    [0, 1],
  ] as const) {
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.translate(side * WING.x, WING.y);
    ctx.rotate(side * (WING.tilt + lift + shift));
    ctx.beginPath();
    ctx.ellipse(side * 4, 0, WING.rx, WING.ry, 0, 0, TAU);
    ctx.fillStyle = VEIL;
    ctx.fill();
    ctx.lineWidth = 1.6;
    ctx.strokeStyle = SOFT;
    ctx.stroke();
    stroke(ctx, FAINT, 1.1, [side * -20, 0], [side * 30, 0]);
    stroke(ctx, FAINT, 1.1, [side * -4, 0], [side * 18, -7]);
    stroke(ctx, FAINT, 1.1, [side * 6, 0], [side * 24, 6]);
    ctx.restore();
  }
}

function needle(ctx: Ctx): void {
  ctx.beginPath();
  ctx.moveTo(-NEEDLE.half, NEEDLE.from);
  ctx.lineTo(0, NEEDLE.to);
  ctx.lineTo(NEEDLE.half, NEEDLE.from);
  ctx.closePath();
  ctx.fillStyle = MASK_BODY;
  ctx.fill();
  ctx.lineJoin = 'round';
  ctx.lineWidth = 2;
  ctx.strokeStyle = MARK;
  ctx.stroke();
}

function bendAt(side: number, u: number): [number, number] {
  const [rx, ry] = FEELER.root;
  const [bx, by] = FEELER.bend;
  const [tx, ty] = FEELER.tip;
  const a = (1 - u) * (1 - u);
  const b = 2 * (1 - u) * u;
  const c = u * u;
  return [side * (a * rx + b * bx + c * tx), a * ry + b * by + c * ty];
}

function feeler(ctx: Ctx, side: number): void {
  const [rx, ry] = FEELER.root;
  const [bx, by] = FEELER.bend;
  const [tx, ty] = FEELER.tip;
  ctx.beginPath();
  ctx.moveTo(side * rx, ry);
  ctx.quadraticCurveTo(side * bx, by, side * tx, ty);
  ctx.lineCap = 'round';
  ctx.lineWidth = 2.2;
  ctx.strokeStyle = MARK;
  ctx.stroke();
  for (let index = 1; index <= FEELER.plumes; index += 1) {
    const [x, y] = bendAt(side, index / (FEELER.plumes + 1));
    const reach = 2.5 + index * 0.45;
    stroke(ctx, SOFT, 1.2, [x - reach, y - reach * 0.8], [x + reach, y + reach * 0.8]);
  }
}

function eyeShape(ctx: Ctx): void {
  ctx.beginPath();
  ctx.ellipse(0, 0, EYE.rx, EYE.ry, 0, 0, TAU);
}

function facets(ctx: Ctx): void {
  ctx.beginPath();
  const across = EYE.facet * Math.sqrt(3);
  for (let row = -6; row <= 6; row += 1) {
    for (let col = -4; col <= 4; col += 1) {
      const cx = col * across + (row % 2 === 0 ? 0 : across / 2);
      const cy = row * EYE.facet * 1.5;
      for (let corner = 0; corner <= 6; corner += 1) {
        const angle = (corner * TAU) / 6 + Math.PI / 6;
        const x = cx + Math.cos(angle) * EYE.facet;
        const y = cy + Math.sin(angle) * EYE.facet;
        if (corner === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      }
    }
  }
  ctx.lineWidth = 0.9;
  ctx.strokeStyle = FAINT;
  ctx.stroke();
}

function compoundEye(ctx: Ctx, pose: Pose, side: number): void {
  ctx.save();
  ctx.translate(side * EYE.x, EYE.y);
  ctx.rotate(side * -EYE.tilt);
  eyeShape(ctx);
  ctx.fillStyle = INK;
  ctx.fill();
  if (!shut(pose)) {
    ctx.save();
    eyeShape(ctx);
    ctx.clip();
    facets(ctx);
    ctx.beginPath();
    ctx.ellipse(-3, -6, 3.4, 2, -0.5, 0, TAU);
    ctx.fillStyle = SOFT;
    ctx.fill();
    ctx.restore();
  }
  eyeShape(ctx);
  ctx.lineWidth = 2.4;
  ctx.strokeStyle = MARK;
  ctx.stroke();
  ctx.restore();
  if (shut(pose)) {
    eye(ctx, pose, side * EYE.x, EYE.y, 6);
  }
}

function moustiques(ctx: Ctx, pose: Pose): void {
  const lift = shut(pose) ? 0 : WING.hum * wave(pose);
  for (const side of [-1, 1]) {
    wing(ctx, side, lift);
  }
  needle(ctx);
  for (const side of [-1, 1]) {
    feeler(ctx, side);
  }
  body(ctx, () => {
    ctx.beginPath();
    ctx.ellipse(0, 0, HEAD.rx, HEAD.ry, 0, 0, TAU);
  });
  for (const side of [-1, 1]) {
    compoundEye(ctx, pose, side);
  }
  stroke(ctx, MARK, 2.2, [-6, 17], [0, 14.5], [6, 17]);
}

function moustiquesGlad(ctx: Ctx): void {
  gladFace(ctx, { eyeY: -8, eyeX: 15, mouthY: 8, mouthWidth: 10, part: 'smile' });
}

export const MOUSTIQUES_FACE: Face = {
  draw: moustiques,
  glad: moustiquesGlad,
  frames: 4,
  loopBeats: 1,
  boss: true,
  flies: true,
};
